<template>
  <div class="fts-users" :data-users-load="loading ? 'loading' : 'loaded'" @scroll="onScroll">
    <div class="user-list-header" ref="clientHeader">
      <div class="user-list-header__l">
        <div class="user-list-tabs">
          <router-link
            v-for="x in tabs"
            :key="x.id"
            :to="{ name: x.routeName }"
            custom
            v-slot="{ navigate }"
          >
            <div
              class="user-list-tab"
              :class="{'user-list-tab--active': tab === x.id}"
              @click="navigate"
            >
              {{x.displayName}}
              <fts-release-badge 
                :stage="x.releaseStage" 
                class="fts-align-top"
                v-if="x.releaseStage"
              />
            </div>
          </router-link>
        </div>
      </div>

      <div class="user-list-header__r">
        <fts-tippy content="Refresh">
          <fts-btn
            slot="trigger"
            icon
            text
            :disabled="loading"
            @click="getMember"
          >
            <font-awesome-icon :icon="['fas', 'sync-alt']" />
          </fts-btn>
        </fts-tippy>
      </div>
    </div>
    <div class="user-list-body">
      <!-- lobby -->
      <div class="user-lobby-form" v-if="tab === 'lobby'">
        <div class="lobby-invitation-container">
          <div class="lobby-invitation-control">
            <fts-toggle-switch
              v-model="lobbyFormData.isEnabled"
              :size="16"
              mat-class
              @change="enableDisableLobby()"
            >
              <div slot="label" class="user-form-label user-form-toggle-label">
                Enable lobby
              </div>
            </fts-toggle-switch>
            <div class="user-form-helper user-form-toggle-helper">All newly joined request will appear below</div>
          </div>

          <fts-transition-fade>
            <div class="lobby-invitation-card" v-if="lobbyFormData.isEnabled && lobbyFormData.cloneInvitationCode">
              <div class="lobby-invitation-label">Code</div>

              <input 
                class="fts-form-control lobby-invitation-form-control" 
                v-model="lobbyFormData.invitationCode" 
                maxlength="10" 
                @keydown.space.prevent 
                ref="invitation-code"
              />

              <fts-btn
                outline
                text-transform-init
                class="lobby-invitation-btn"
                v-if="!lobbyFormData.invitationCode.length"
                @click="lobbyFormData.invitationCode = lobbyFormData.cloneInvitationCode"
              >
                Cancel
              </fts-btn>

              <fts-btn
                outline
                text-transform-init
                color="primary"
                :loading="submitting"
                class="lobby-invitation-btn"
                v-else-if="lobbyFormData.invitationCode != lobbyFormData.cloneInvitationCode"
                @click="updateLobbyInvitationCode"
              >
                Save
              </fts-btn>

              <fts-clipboard class="lobby-invitation-link" :copy="getInviteMessage">
                {{lobbyFormData.invitationLink}}
              </fts-clipboard>
            </div>
          </fts-transition-fade>
        </div>
      </div>
      <!-- control -->
      <div class="user-list-control">
        <div class="user-list-control__l" v-if="tab != 'lobby'">
          <input
            type="text"
            class="fts-form-control user-list-form-control"
            placeholder="Search by User"
            v-model.trim="searchDisplayName"
            @input="search"
          />

          <fts-badge 
            color="text"
            :tooltip="`Total users: ${getUsers.length}`"
            variant="pill"
            v-if="tab === 'active' && getUsers && getUsers.length"
          >
            {{getUsers.length}}
          </fts-badge>
        </div>

        <div class="user-list-control__r">
          <fts-dropdown-select
            :options="roles"
            @selected="setFilters"
            v-if="(tab === 'active' || tab === 'inactive')"
          >
            <fts-btn
              slot="toggle"
              color="primary"
              text
              text-transform-init
            >
              Filter
            </fts-btn>

            <div
              slot="option" 
              slot-scope="{ option, defaultClass, select }"
              :class="defaultClass"
              @click="select(option)"
            >
              Filter: {{option.name}}
            </div>
          </fts-dropdown-select>

          <fts-btn
            color="primary"
            outline
            text-transform-init
            @click="openInvitation"
            v-if="tab !== 'lobby'"
          >
            <font-awesome-icon :icon="['fas', 'user-plus']" /> Invite
          </fts-btn>
        </div>
      </div>
      <!-- filter -->
      <div 
        class="user-list-filter" 
        v-if="(tab === 'active' || tab === 'inactive') && filters && filters.length"
      >
        <div
          v-for="(x, ind) in filters"
          :key="x.id"
          class="user-list-filter-tag"
        >
          {{x.name}}
          <font-awesome-icon
            :icon="['fas', 'times']"
            class="user-list-filter-tag-action"
            @click="removeFilter(ind)"
          />
        </div>
      </div>
      <!-- users -->
      <div class="user-list-container user-list-users" v-if="userListReady">
        <!-- not found -->
        <fts-result-not-found 
          :show="error" 
          :error="true" 
          :creatable="false" 
          v-if="error" 
        />        

        <div class="user-list-card" v-else-if="getUsers && getUsers.length">
          <div
            class="user-list-wrapper"
            v-for="(user, index) in getUsers"
            :key="`${user.userProfileId}-${index}`"
          >
            <div class="user-upr-row">
              <!-- avatar -->
              <div class="user-avatar">
                <fts-profile
                  :profile="{
                    displayName: user.displayName,
                    profilePictureUrl: user.profilePictureUrl,
                  }"
                  :profilePhotoSize="40"
                  :profileName="false"
                />
              </div>
              <!-- user info -->
              <div class="user-info fts-row gutters">
                <div class="fts-col fts-col-md-8">
                  <div class="user-info-wrapper">
                    <fts-tippy
                      trigger-class="user-info-name"
                      :content="user.displayName || user.userName"
                      class="user-info-name-wrapper"
                      @click.native.prevent="openUserModal(user)"
                    >
                      {{user.displayName || user.userName}}
                    </fts-tippy>
                    <div class="user-roles">
                      <fts-badge
                        size="sm"
                        tooltip="Workspace owner"
                        class="user-role-wrapper"
                        v-if="user.owner"
                      >
                        Owner
                      </fts-badge>

                      <fts-badge
                        size="sm"
                        :tooltip="consultant.description"
                        class="user-role-wrapper"
                        v-if="user.consultant"
                      >
                        {{consultant.label}}
                      </fts-badge>

                      <fts-badge
                        size="sm"
                        :tooltip="workspaceAdmin.description"
                        class="user-role-wrapper"
                        v-if="user.workspaceAdmin"
                      >
                        {{workspaceAdmin.label}}
                      </fts-badge>

                      <fts-badge
                        size="sm"
                        :tooltip="userAdmin.description"
                        class="user-role-wrapper"
                        v-if="user.userAdmin"
                      >
                        {{userAdmin.label}}
                      </fts-badge>
                    </div>
                  </div>
                  <div class="user-info-username">
                    {{user.userName}}
                  </div>
                </div>
                <div class="fts-col fts-col-md-4" v-if="user.phoneNumber">
                  <div class="user-info-phone">
                    <font-awesome-icon
                      :icon="['fas', 'phone']"
                      fixed-width
                      class="user-info-phone-icon"
                    />
                    <div class="user-info-phone-number">{{user.phoneNumber}}</div>
                  </div>
                </div>
              </div>
              <!-- actions -->
              <div class="user-actions">
                <template v-if="tab === 'lobby'">
                  <fts-btn color="primary" sm outline text-transform-init class="user-action-btn" @click="acceptUser(user)">
                    Accept
                  </fts-btn>

                  <fts-btn color="danger" sm outline text-transform-init class="user-action-btn" @click="rejectUser(user)">
                    Reject
                  </fts-btn>
                </template>

                <div class="user-status" v-if="tab !== 'lobby'">
                  <fts-badge
                    :color="user.status == 'active' ? 'success' : 'danger'"
                    class="fts-capitalize"
                    v-if="user.status"
                  >
                    {{user.status}}
                  </fts-badge>

                  <font-awesome-icon
                    :icon="['fas', 'lock']"
                    class="user-status-locked"
                    v-if="user.isLocked"
                  />
                </div>

                <fts-dropdown-select
                  class="user-action-dropdown"
                  append-content-class="user-action-content user-action-dropdown-menu"
                  :offset="[0, 6]"
                  placement="left-start"
                  @selected="executeAction($event, user)"
                  v-if="tab !== 'lobby'"
                >
                  <fts-btn slot="toggle" icon text class="user-action-toggle">
                    <font-awesome-icon :icon="['fas', 'ellipsis-v']" />
                  </fts-btn>
                  <template #menu="{ select, defaultOptionClass }">
                    <!-- reset password -->
                    <div
                      class="user-action-option"
                      :class="defaultOptionClass"
                      @click="select('resetPassword')"
                    >
                      <div class="user-action-option-icon">
                        <font-awesome-icon :icon="['fas', 'key']" fixed-wdith />
                      </div>
                      <div class="user-action-option-r">
                        <div class="user-action-option-label">Reset Password</div>
                        <div class="user-action-option-helper">
                          Request to reset a new password.
                        </div>
                      </div>
                    </div>
                    <!-- enable member -->
                    <div
                      class="user-action-option"
                      :class="defaultOptionClass"
                      @click="select('disable')"
                      v-if="user.disabled"
                    >
                      <div class="user-action-option-icon">
                        <font-awesome-icon :icon="['fas', 'user-check']" />
                      </div>
                      <div class="user-action-option-r">
                        <div class="user-action-option-label">Enable {{member.label}}</div>
                        <div class="user-action-option-helper">
                          {{member.description}}
                        </div>
                      </div>
                    </div>
                    <!-- disable member -->
                    <div
                      class="user-action-option"
                      :class="defaultOptionClass"
                      @click="select('disable')"
                      v-else
                    >
                      <div class="user-action-option-icon">
                        <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                      </div>
                      <div class="user-action-option-r">
                        <div class="user-action-option-label">Disable {{member.label}}</div>
                        <div class="user-action-option-helper">
                          {{member.description}}
                        </div>
                      </div>
                    </div>
                    <!-- unlock user -->
                    <div
                      class="user-action-option"
                      :class="defaultOptionClass"
                      @click="select('lock')"
                      v-if="user.isLocked"
                    >
                      <div class="user-action-option-icon">
                        <font-awesome-icon :icon="['fas', 'unlock']" fixed-width />
                      </div>
                      <div class="user-action-option-r">
                        <div class="user-action-option-label">Unlock User</div>
                        <div class="user-action-option-helper">
                          Activate the user to access the workspace.
                        </div>
                      </div>
                    </div>
                    <!-- consultant -->
                    <template v-if="isAdministrator">
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('consultant')"
                        v-if="!user.consultant"
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Set as {{consultant.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{consultant.description}}
                          </div>
                        </div>
                      </div>
                      <!-- remove workspace admin -->
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('consultant')"
                        v-else
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Remove {{consultant.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{consultant.description}}
                          </div>
                        </div>
                      </div>
                    </template>
                    <!-- workspace admin -->
                    <template v-if="isAdminUser">             
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('workspaceAdmin')"
                        v-if="!user.workspaceAdmin"
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Set as {{workspaceAdmin.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{workspaceAdmin.description}}
                          </div>
                        </div>
                      </div>
                      <!-- remove workspace admin -->
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('workspaceAdmin')"
                        v-else
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Remove {{workspaceAdmin.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{workspaceAdmin.description}}
                          </div>
                        </div>
                      </div>
                    </template>
                    <!-- user admin -->
                    <template v-if="isAdminUser || isManagerUser">
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('userAdmin')"
                        v-if="!user.userAdmin"
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-check']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Set as {{userAdmin.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{userAdmin.description}}
                          </div>
                        </div>
                      </div>
                      <!-- remove user admin -->
                      <div
                        class="user-action-option"
                        :class="defaultOptionClass"
                        @click="select('userAdmin')"
                        v-else
                      >
                        <div class="user-action-option-icon">
                          <font-awesome-icon :icon="['fas', 'user-times']" fixed-width />
                        </div>
                        <div class="user-action-option-r">
                          <div class="user-action-option-label">
                            Remove {{userAdmin.label}}
                          </div>
                          <div class="user-action-option-helper">
                            {{userAdmin.description}}
                          </div>
                        </div>
                      </div>
                    </template>
                  </template>
                </fts-dropdown-select>
              </div>
            </div>
            <!-- user profile -->
            <div class="user-profile-section" v-if="!user.isExternalUser">
              <div class="user-profile-container">
                <fts-btn
                  sm
                  outline
                  textTransformInit
                  :color="x.active ? 'success' : 'danger'"
                  v-for="x in getUserProfiles(user.profiles)"
                  :key="x.id"
                  @click="
                    openQuickView({
                      locusId: x.entryId,
                      moduleId: x.id,
                      user: user,
                    })
                  "
                >
                  <font-awesome-icon
                    :icon="['fas', 'circle']"
                    class="user-profile-state"
                  />
                  <fts-moji :emoji="x.emoji" :size="14" v-if="x.emoji" />
                  <span class="user-profile-name">{{ x.name }}</span>
                  <font-awesome-icon
                    :icon="['far', 'trash-alt']"
                    class="user-profile-action stopPropagation"
                    @click="
                      deleteUserProfile({
                        locusId: x.entryId,
                        moduleId: x.id,
                        user: user,
                      })
                    "
                  />
                </fts-btn>
                <fts-dropdown-select
                  append-content-class="profile-content profile-dropdown-menu"
                  @selected="openQuickView({ moduleId: $event.id, user: user })"
                  v-if="getAvailableUserProfiles(user.profiles).length">
                  <fts-btn
                    slot="toggle"
                    text
                    sm
                    text-transform-init
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                    <span class="profile-toggle-label">create profile</span>
                  </fts-btn>
                  <template #menu="{ select, defaultOptionClass }">
                    <div
                      class="profile-option"
                      :class="defaultOptionClass"
                      v-for="x in getAvailableUserProfiles(user.profiles)"
                      :key="x.id"
                      @click="select(x)"
                    >
                      <fts-moji
                        :emoji="x.emoji"
                        :size="16"
                        class="profile-option-emoji"
                        v-if="x.emoji"
                      />
                      <div class="profile-option-name">{{ x.name }}</div>
                    </div>
                  </template>
                </fts-dropdown-select>
              </div>
            </div>
          </div>
        </div>

        <fts-result-not-found 
          :show="true" 
          :search="true" 
          :creatable="false" 
          v-else-if="hasSearch" 
        />  

        <div class="user-list-empty" v-else>
          <div class="user-list-empty__icon">
            <img :src="emptyUserImage" alt="empty-user-image">
          </div>

          <div class="user-list-empty__title">No results</div>

          <div class="user-list-empty__description">
            Looks a little bit empty here. Add members to manage access and collaboration.
          </div>
        </div>
      </div>
    </div>
    <!-- reset password modal -->
    <fts-client-modal
      title="Request Reset Password"
      @closed="resetModal"
      ref="resetPasswordModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['fas', 'key']" fixed-width />
      <!-- body -->
      <template #body>
        <!-- temporary password -->
        <template v-if="modalFormData.temporaryPassword">
          <div class="confirmation-desc">
            Temporary password for {{ modalFormData.userName }} is:
          </div>
          <fts-clipboard :copy="modalFormData.temporaryPassword" class="confirmation-clipboard">
            {{modalFormData.temporaryPassword}}
          </fts-clipboard>
        </template>
        <!-- request -->
        <template v-else>
          <div class="confirmation-desc">
            This will replace {{ modalFormData.userName }}'s current password with
            a new password.
          </div>
          <fts-btn
            color="primary"
            outline
            text-transform-init
            :loading="modalLoading"
            class="confirmation-action"
            @click="resetPassword"
          >
            Request New Password
          </fts-btn>
        </template>
      </template>
      <!-- footerRight -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeResetPasswordModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- user unlock modal -->
    <fts-client-modal
      title="Unlock user account"
      @closed="resetModal"
      ref="userUnlockModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['fas', 'unlock']" fixed-width />
      <!-- body -->
      <template #body>
        <div class="confirmation-desc">
          It will allow {{ modalFormData.userName }} access to this workspace.
        </div>
      </template>
      <!-- footerRight -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeUserUnlockModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>

        <fts-btn
          color="primary" 
          :loading="modalLoading" 
          @click="unlock"
        >
          <font-awesome-icon :icon="['fas', 'unlock']" /> Unlock
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- user access modal -->
    <fts-client-modal
      :title="modalFormData.disabled ? 'Enable user for access?' : 'Disable user for access?'"
      @closed="resetModal"
      ref="userAccessModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['fas', 'user-check']" fixed-width v-if="modalFormData.disabled" />
      <font-awesome-icon slot="icon" :icon="['fas', 'user-times']" fixed-width v-else />
      <!-- body -->
      <template #body>
        <template v-if="modalFormData.disabled">
          <div class="confirmation-desc">
            It will allow {{ modalFormData.userName }} access to this workspace.
          </div>
          <div class="confirmation-subtext">
            You can block access by disable the member later.
          </div>
        </template>

        <template v-else>
          <div class="confirmation-desc">
            It will block {{ modalFormData.userName }}'s access to this
            workspace.
          </div>
          <div class="confirmation-subtext">
            You can restore access by enable the member later.
          </div>
        </template>
      </template>
      <!-- footerRight -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeUserAccessModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>

        <fts-btn
          color="primary"
          :loading="modalLoading"
          @click="setDisableEnable"
        >
          <template v-if="modalFormData.disabled">
            <font-awesome-icon :icon="['fas', 'user-check']" /> Enable
          </template>
          <template v-else>
            <font-awesome-icon :icon="['fas', 'user-times']" /> Disable
          </template>
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- user modal -->
    <fts-client-modal
      :title="`${userId ? 'Update': 'Create' } User`"
      @closed="resetUserModal"
      ref="userModal"
    >
      <!-- header:icon -->
      <font-awesome-icon slot="icon" :icon="['far', 'user']" fixed-width />
      <!-- body -->
      <template #body>
        <template v-for="x in fields">
          <fts-form-element
            :item="x"
            :form-data="userModalFormData"
            :validator="$v.userModalFormData"
            comfort-design
            :key="x.id"
          />
        </template>
      </template>
      <!-- footer:right -->
      <template #footerRight>
        <fts-btn
          text
          :disabled="modalLoading"
          @click="closeUserModal"
        >
          <font-awesome-icon :icon="['fas', 'times']" /> Close
        </fts-btn>

        <fts-btn
          color="primary"
          :loading="modalLoading"
          @click.native.prevent="updateMember"
          v-if="userId"
        >
          <font-awesome-icon :icon="['fas', 'check']" /> {{userId ? 'Update': 'Create'}}
        </fts-btn>
      </template>
    </fts-client-modal>
    <!-- spinner -->
    <fts-spinner full-screen boundary="parent" v-if="loading" />
    <!-- invitation -->
    <fts-invitation-modal ref="ftsInvitationModal" />
  </div>
</template>

<script>
import Vue from 'vue';
import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);
import moment from 'moment';

import commons from '../commons/commons';
import { passwordValidator } from '../commons/validator';
import { authorizationStore } from '../store/authorizationStore';
import { clientStore } from '../store/clientStore';
import { ReleaseStage } from '../commons/enum';
import { emptyUserImage } from "../commons/images";

import FTSBadge from '../elements/FTSBadge';
import FTSBtn from '../elements/FTSBtn';
import FTSClientModal from '../elements/FTSClientModal';
import FTSClipboard from '../elements/FTSClipboard';
import FTSCollapsibleElement from '../elements/FTSCollapsibleElement';
import FTSDropdownSelect from '../elements/FTSDropdownSelect';
import FTSFormElement from '../elements/FTSFormElement';
import FTSInvitationModal from '../client/FTSInvitationModal';
import FTSLabelGroup from '../elements/FTSLabelGroup';
import FTSMoji from '../elements/FTSMoji';
import FTSProfile from '../elements/FTSProfile';
import FTSResultNotFound from '../elements/error/FTSResultNotFound';
import FTSSpinner from '../elements/FTSSpinner';
import FTSTippy from '../elements/FTSTippy';
import FTSToggleSwitch from '../elements/FTSToggleSwitch';
import FTSTransitionFade from '../elements/transition/FTSTransitionFade';

export default {
  data: () => ({
    users: [],
    searchDisplayName: "",
    loading: false,
    userListReady: false,
    userListLoadToken: 0,
    submitting: false,
    error: false,
    hasSearch: false,
    modalLoading: false,
    modalFormData: {},
    userModalFormData: {},
    filters: [],
    lobbyFormData: {
      isEnabled: false,
      invitationCode: "",
      cloneInvitationCode: "",
      invitationLink: "",
    },
    emptyUserImage
  }),
  components: {
    "fts-badge": FTSBadge,
    "fts-btn": FTSBtn,
    "fts-client-modal": FTSClientModal,
    "fts-clipboard": FTSClipboard,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-form-element": FTSFormElement,
    "fts-invitation-modal": FTSInvitationModal,
    "fts-label-group": FTSLabelGroup,
    "fts-moji": FTSMoji,
    "fts-profile": FTSProfile,
    "fts-result-not-found": FTSResultNotFound,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-transition-fade": FTSTransitionFade
  },
  validations() {
    let userModalFormData = {};
    userModalFormData = commons.getFormValidators(this.fields);
    return { userModalFormData };
  },
  methods: {
    moment,
    onScroll(evt) {
      try {
        const isScroll = evt.target.scrollTop > 0;
        if (isScroll)
          this.$refs.clientHeader.classList.add('user-list-header--scroll');
        else if (!isScroll)
          this.$refs.clientHeader.classList.remove('user-list-header--scroll');
      } catch(err) {
        console.warn("[DOMException]", err);
      }
    },
    search: _.debounce(async function () {
      await this.getMember();
      this.hasSearch = this.searchDisplayName ? true : false;
    }, 500),
    getUserProfiles(profiles) {
      return this.workspaceProfiles.reduce((list, x) => {
        const userProfile = profiles.find((y) => y.moduleId == x.id);
        if (userProfile) {
          list.push({
            id: x.id,
            name: x.name,
            emoji: x.emoji,
            entryId: userProfile.entryId,
            active: userProfile.active,
          });
        }
        return list;
      }, []);
    },
    getAvailableUserProfiles(profiles) {
      return this.workspaceProfiles.reduce((list, x) => {
        const userProfile = profiles.find((y) => y.moduleId == x.id);
        if (!userProfile) {
          list.push({
            id: x.id,
            name: x.name,
            emoji: x.emoji,
          });
        }
        return list;
      }, []);
    },
    async executeAction(type, selectedItem) {
      if (
        type == "workspaceAdmin" ||
        type == "userAdmin" ||
        type == "consultant"
      ) {
        try {
          await clientStore.dispatch("setRole", {
            userProfileId: selectedItem.userProfileId,
            workspaceAdmin:
              type == "workspaceAdmin"
                ? !selectedItem.workspaceAdmin
                : selectedItem.workspaceAdmin,
            userAdmin:
              type == "userAdmin"
                ? !selectedItem.userAdmin
                : selectedItem.userAdmin,
            consultant:
              type == "consultant"
                ? !selectedItem.consultant
                : selectedItem.consultant,
          });
          await this.getMember();
          this.$toasted.success(`User role have been updated`);
        } catch (err) {
          this.$toasted.global.error({
            response: err.response,
            message: "Failed to update user role",
          });
        }
      } else if (type == "resetPassword") {
        this.modalFormData = _.clone(selectedItem);
        this.$refs.resetPasswordModal.show();
      } else if (type == "lock") {
        this.modalFormData = _.clone(selectedItem);
        this.$refs.userUnlockModal.show();
      } else if (type == "disable") {
        this.modalFormData = _.clone(selectedItem);
        this.$refs.userAccessModal.show();
      }
    },
    closeResetPasswordModal() {
      this.$refs.resetPasswordModal.close();
    },
    closeUserUnlockModal() {
      this.$refs.userUnlockModal.close();
    },
    closeUserAccessModal() {
      this.$refs.userAccessModal.close();
    },
    resetModal() {
      this.modalFormData = {};
    },
    openUserModal(user) {
      this.$refs.userModal.show();
      this.userModalFormData = user && user.userProfileId ? _.clone(user) : {};
    },
    closeUserModal() {
      this.$refs.userModal.close();
    },
    resetUserModal() {
      this.userModalFormData = {};
      this.$v.userModalFormData.$reset();
    },
    openQuickView(data) {
      if (commons.elementStopPropagation()) return;

      if (data.moduleId && data.locusId) {
        window.eventBus.$emit("quickViewData", {
          id: data.locusId,
          vectorId: data.moduleId,
          username: data.user.userName,
        });
      } else if (data.moduleId) {
        window.eventBus.$emit("quickViewData", {
          vectorId: data.moduleId,
          activityId: "create",
          username: data.user.userName,
        });
      }
    },
    deleteUserProfile(data) {
      if (data.moduleId && data.locusId) {
        window.eventBus.$emit("quickViewData", {
          id: data.locusId,
          vectorId: data.moduleId,
          activityId: "delete",
          username: data.user.userName,
        });
      }
    },
    openInvitation() {
      this.$refs.ftsInvitationModal.open({ workspaceName: clientStore.getters.workspaceName });
    },
    createUserListLoadToken(resetUserListReady) {
      if (!resetUserListReady) {
        return this.userListLoadToken;
      }

      this.userListReady = false;
      this.userListLoadToken += 1;
      return this.userListLoadToken;
    },
    isCurrentUserListLoad(loadToken) {
      return loadToken === this.userListLoadToken;
    },
    async loadLobby(options = {}) {
      const resetUserListReady = Boolean(options && options.resetUserListReady);
      const loadToken = options && options.loadToken
        ? options.loadToken
        : this.createUserListLoadToken(resetUserListReady);
      this.loading = true;
      this.error = false;

      try {
        const res = await clientStore.dispatch("loadLobby", {});
        if (resetUserListReady && !this.isCurrentUserListLoad(loadToken)) return;

        this.users = res.data.users;
        this.lobbyFormData.isEnabled = res.data.enable;
        this.lobbyFormData.invitationCode = res.data.code;
        this.lobbyFormData.cloneInvitationCode = res.data.code;
        this.lobbyFormData.invitationLink = res.data.lobbyLink;

      } catch (err) {
        if (resetUserListReady && !this.isCurrentUserListLoad(loadToken)) return;
        this.users = [];
        this.error = true;
        this.$toasted.error("Failed to retrieve users");
      } finally {
        if (resetUserListReady && !this.isCurrentUserListLoad(loadToken)) return;
        this.loading = false;
        if (resetUserListReady) {
          this.userListReady = true;
        }
      }
    },
    async getMember(options = {}) {
      const resetUserListReady = Boolean(options && options.resetUserListReady);
      const loadToken = this.createUserListLoadToken(resetUserListReady);

      if (this.tab === "lobby") {
        await this.loadLobby({ resetUserListReady, loadToken });
      } else {
        this.loading = true;
        this.error = false;
        try {
          const res = await clientStore.dispatch("getMember", {
            searchDisplayName: this.searchDisplayName,
            type: this.tab
          });
          if (resetUserListReady && !this.isCurrentUserListLoad(loadToken)) return;

          this.users = res.data.map((x) => ({
            ...x,
            status: x.disabled ? "inactive" : "active",
          }));
        } catch (err) {
          if (resetUserListReady && !this.isCurrentUserListLoad(loadToken)) return;
          this.users = [];
          this.error = true;
          this.$toasted.error("Failed to retrieve users");
        } finally {
          if (resetUserListReady && !this.isCurrentUserListLoad(loadToken)) return;
          this.loading = false;
          if (resetUserListReady) {
            this.userListReady = true;
          }
        }
      }
    },
    async memberRegistration() {
      this.$v.userModalFormData.$touch();
      if (this.$v.userModalFormData.$invalid) {
        return this.$toasted.global.invalidFormValidate();
      } else {
        this.modalLoading = true;
        let customError = false;
        const formData = Object.assign({}, this.userModalFormData);
        try {
          let res = await clientStore.dispatch("memberRegistration", formData);
          if (res.data.result.succeeded) {
            await this.getMember();
            this.$toasted.success("Successfully created");
            this.closeUserModal();
          } else {
            customError = true;
            throw res.data.result.errors;
          }
        } catch (err) {
          customError
            ? this.$toasted.error(err)
            : this.$toasted.error(`Failed to create`);
        }
        this.modalLoading = false;
      }
    },
    async updateMember() {
      this.$v.userModalFormData.$touch();
      if (this.$v.userModalFormData.$invalid) {
        return this.$toasted.global.invalidFormValidate();
      } else {
        this.modalLoading = true;
        const formData = Object.assign({}, this.userModalFormData);
        try {
          await clientStore.dispatch("updateMember", {
            ...formData,
            id: formData.userProfileId,
          });
          await this.getMember();
          this.$toasted.success("Successfully updated");
        } catch (err) {
          this.$toasted.error(`Failed to update`);
        }

        this.closeUserModal();
        this.modalLoading = false;
      }
    },
    async resetPassword() {
      this.modalLoading = true;
      try {
        let res = await clientStore.dispatch("resetUserPassword", {
          id: this.modalFormData.userProfileId,
        });
        this.modalFormData.temporaryPassword = res.data.password;
        this.$toasted.success("Password reset successfully");
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
          message: "Failed to reset password",
        });
        this.closeResetPasswordModal();
      }
      this.modalLoading = false;
    },
    async setDisableEnable() {
      this.modalLoading = true;
      try {
        await clientStore.dispatch("setDisableEnable", {
          id: this.modalFormData.userProfileId,
        });
        await this.getMember();
        this.$toasted.success(
          `User access has been ${
            this.modalFormData.disabled ? "granted" : "denied"
          }`
        );
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
          message: "Failed to update user access",
        });
      }
      this.closeUserAccessModal();
      this.modalLoading = false;
    },
    async unlock() {
      this.modalLoading = true;
      try {
        await clientStore.dispatch("unlockUser", {
          id: this.modalFormData.userProfileId,
        });
        await this.getMember();
        this.$toasted.success(
          `User access has been ${
            this.modalFormData.isLocked ? "unlock" : "locked"
          }`
        );
      } catch (err) {
        this.$toasted.global.error({
          response: err.response,
          message: "Failed to update user access",
        });
      }
      this.closeUserUnlockModal();
      this.modalLoading = false;
    },
    /* filters */
    setFilters(data) {
      const findIndex = this.filters.findIndex(x => x.id === data.id);
      if (findIndex > -1) {
        this.removeFilter(findIndex);
      } else {
        this.filters.push(data);
      }
    },
    removeFilter(index) {
      this.filters.splice(index, 1);
    },
    /* invitation */
    relativeTime(data) {
      return commons.momentRelativeTime(data);
    },
    async acceptUser(user) {
      try {
          const res = await clientStore.dispatch("acceptRejectUser", {
            userId: user.userProfileId,
          });
          await this.loadLobby();
        this.$toasted.success(`User ${user.userName} has been successfully accepted into the workspace`);
        } catch(err) {
          this.$toasted.global.error(err);
        }
    },
    async rejectUser(user) {
      try {
          const res = await clientStore.dispatch("acceptRejectUser", {
            userId: user.userProfileId,
            reject: true
          });
          await this.loadLobby();
        this.$toasted.success(`User ${user.userName} has been rejected into the workspace. To revert, please go to disabled members to enable`);
      } catch(err) {
        this.$toasted.global.error(err);
      }
    },
    async enableDisableLobby() {
      const res = await clientStore.dispatch("updateLobby", {
        enable: this.lobbyFormData.isEnabled
      });

      await this.loadLobby();
    },
    async updateLobbyInvitationCode() {
      try {
        this.submitting = true;
        const res = await clientStore.dispatch("updateLobby", {
          enable: this.lobbyFormData.isEnabled,
          code: this.lobbyFormData.invitationCode
        });

        await this.loadLobby();
        this.$toasted.success("The invitation link has been updated successfully. You may share the invite link!");
        this.submitting = false;
      } catch(error) {
        console.info("error", error)
        this.$toasted.global.error(error);
        this.submitting = false;
        this.$nextTick(() => {
          this.$refs['invitation-code'].focus();
        });
      }
    }
  },
  computed: {
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    tabs() {
      const tabs = [
        { id: "lobby", routeName: "lite-users-lobby", displayName: "Lobby" },
        { id: "active", routeName: "lite-users", displayName: "Active" },
        { id: "inactive", routeName: "lite-users-inactive", displayName: "Inactive" }
      ];

      return tabs.filter(x => {
        if (x.releaseStage) {
          if (x.releaseStage === ReleaseStage.ALPHA) {
            return this.isInternal;
          } else if (x.releaseStage === ReleaseStage.DEVELOPMENT) {
            return this.isDevelopment;
          }
        }

        return true;
      });
    },
    tab() {
      switch(this.$route.name) {
        case "lite-users-lobby":
          return "lobby"
        case "lite-users-inactive":
          return "inactive";
        case "lite-users-invitation":
          return "invitation";
        default:
          return "active";
      }
    },
    fields() {
      return [
        {
          type: 0,
          fieldName: "userName",
          required: !this.userId,
          readOnly: this.userId,
        },
        {
          input: "password",
          fieldName: "password",
          required: true,
          visible: !this.userId,
          validator: {
            password: passwordValidator,
          },
        },
        {
          type: 0,
          fieldName: "displayName",
          required: !this.userId,
        },
        {
          type: 0,
          fieldName: "firstName",
          readOnly: this.userId,
        },
        {
          type: 0,
          fieldName: "lastName",
          readOnly: this.userId,
        },
        {
          type: 26,
          fieldName: "phoneNumber",
        },
        {
          type: 25,
          fieldName: "email",
        },
      ].filter((x) => x.visible != false);
    },
    workspaceId() {
      return clientStore.getters.workspaceId;
    },
    workspaceModules() {
      return clientStore.getters.workspaceModules;
    },
    workspaceProfiles() {
      return clientStore.getters.workspaceModules.filter((x) => x.isProfile);
    },
    userId() {
      return this.userModalFormData && this.userModalFormData.userProfileId;
    },
    isAdministrator() {
      return authorizationStore.getters.isAdministrator;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    isManagerUser() {
      return authorizationStore.getters.isManagerUser;
    },
    getUsers() {
      return this.filters && this.filters.length ?
        this.users.filter(x => this.filters.some(y => x[y.id])) : this.users;
    },
    getInviteMessage() {
      return "You're invited to join the Inistate workspace " + clientStore.getters.workspaceName + "!\n" +
        "\n" + 
        "Join now: " + this.lobbyFormData.invitationLink + "\n" +
        "\n" +
        "Get the app: https://inistate.com/download\n" +
        "Your code: " + this.lobbyFormData.cloneInvitationCode;
    }
  },
  watch: {
    tab() {
      this.getMember({ resetUserListReady: true });
    }
  },
  beforeMount() {
    if (!(authorizationStore.getters.isAdminUser || authorizationStore.getters.isManagerUser)) {
      this.$toasted.error("Access Denied. Please refer to your system administrator.");
      this.$router.push({ name: 'dashboard' });
    }
  },
  created() {
    this.roles = [
      { id: "consultant", name: "Consultant"},
      { id: "workspaceAdmin", name: "Workspace Admin"},
      { id: "userAdmin", name: "User Admin"}
    ];
    this.consultant = {
      label: "Consultant",
      description: "Consultant can access studio, change workspace settings and invite new members to the workspace."
    };
    this.workspaceAdmin = {
      label: "Workspace Admin",
      description: "Workspace Admin can access studio, change workspace settings and invite new members to the workspace."
    };
    this.userAdmin = {
      label: "User Admin",
      description: "User Admin can invite new members to the workspace."
    };
    this.member = {
      label: "Member",
      description: "Member cannot change workspce settings or invite new members to the workspace."
    };
  },
  async mounted() {
    await this.getMember({ resetUserListReady: true });
    window.eventBus.$on("users:refresh", this.getMember);
  },
  beforeDestroy() {
    window.eventBus.$off("users:refresh", this.getMember);
  }
};
</script>

<style lang="scss">
@use "sass:color";
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

/* ==========================================================================
   Calm density — P1 (2026-09-05, round 4): docs/frontend-lite-pages-calm-density-proposal.md,
   rulings U1–U4 (recommended options). Class-level only: tab texts, the "Search by User"
   textbox, the Invite / Filter buttons, the action dropdown and every .user* hook survive.
   Placed first; the tripled root class out-ranks the original rules below at every depth.
   ========================================================================== */
.fts-users.fts-users.fts-users {

  /* U1 — header: a 48px hairline bar, tabs weight-not-hue */
  .user-list-header {
    margin: 0;
    padding: 0 24px;
    min-height: 48px;
    align-items: center;
    border-radius: 0;
    border-bottom: 1px solid var(--fts-gray-100);
    background-color: var(--inistate-color-shade-white);
    box-shadow: none !important;
    .user-list-tabs {
      align-self: stretch;
      align-items: flex-end;
      gap: 16px;
      padding-top: 0;
      .user-list-tab {
        padding: 14px 0 12px;
        margin-bottom: -1px;
        font-size: 0.8125rem;
        line-height: 20px;
        color: var(--inistate-text-secondary);
        border-bottom: 2px solid transparent;
        &.user-list-tab--active {
          color: var(--fts-text);
          font-weight: 600;
          border-color: var(--fts-text);
        }
      }
    }
    .user-list-header__r .fts-btn {
      height: 32px;
      width: 32px;
      min-height: 32px;
      padding: 0;
      color: var(--fts-default);
      &:hover { color: var(--fts-text); }
    }
  }

  .user-list-body {
    min-width: 0;
    padding: 0 0 24px;

    /* U2 — controls: 32px search, digits count, Filter text-weight, Invite the one accent */
    .user-list-control {
      align-items: center;
      padding: 12px 24px 0;
      .user-list-control__l .fts-badge {
        margin-left: 8px;
        padding: 0;
        background-color: transparent !important;
        color: var(--inistate-text-secondary) !important;
        font-size: 0.75rem;
        font-weight: 400;
        .badge-wrapper { padding: 0; background-color: transparent !important; }
      }
      .user-list-control__r {
        margin-left: 12px;
        .fts-btn {
          height: 32px;
          min-height: 32px;
          padding: 0 10px;
          font-size: 0.8125rem;
          letter-spacing: 0;
          &.fts-btn-text {
            color: var(--inistate-text-secondary) !important;
            &:hover { color: var(--fts-text) !important; background-color: transparent !important; }
          }
          &.fts-btn-outline {
            background-color: var(--fts-primary) !important;
            border-color: var(--fts-primary) !important;
            color: #fff !important;
          }
        }
      }
    }
    .user-list-form-control {
      max-width: 220px;
      height: 32px;
      min-height: 32px;
      padding: 0 10px;
      font-size: 0.8125rem;
      border-color: var(--fts-gray-200);
    }
    .user-list-control + .user-list-filter { padding: 8px 24px 0; }
    .user-list-filter .user-list-filter-tag {
      padding: 2px 8px;
      border-color: var(--fts-gray-200);
      border-radius: var(--inistate-rounded-4);
      background-color: transparent;
      font-size: 0.75rem;
    }

    /* U3 — member rows: 48px on hairlines, roles as text, grey hover */
    .user-list-container {
      margin-top: 8px;
      .user-list-card {
        border-radius: 0;
        background-color: transparent;
        box-shadow: none;
      }
      .user-list-wrapper {
        min-height: 48px;
        padding: 0 24px;
        justify-content: center;
        border-bottom: 1px solid var(--fts-gray-100) !important;
      }
    }
    .user-list-users .user-list-wrapper {
      &:hover {
        background-color: var(--inistate-color-neutral-50);
        .user-info-name { color: var(--fts-text); }
        .user-action-dropdown { opacity: 1; }
      }
      .user-upr-row { min-height: 48px; align-items: center; }
      .user-avatar {
        margin-right: 12px;
        .profile-photo {
          width: 28px !important;
          height: 28px !important;
          min-width: 28px !important;
        }
      }
      .user-info { gap: 0; }
      .user-info-name {
        font-size: 0.8125rem;
        line-height: 16px;
        font-weight: 500;
        color: var(--fts-text);
      }
      .user-roles {
        margin-left: 8px;
        .user-role-wrapper {
          padding: 0;
          background-color: transparent !important;
          color: var(--inistate-text-secondary) !important;
          font-size: 0.75rem;
          line-height: 16px;
          font-weight: 400;
          .badge-wrapper { padding: 0; background-color: transparent !important; }
          &:not(:first-child) {
            margin-left: 0;
            &::before { content: "·"; margin: 0 6px; color: var(--fts-gray-300); }
          }
        }
      }
      .user-info-username {
        margin-top: 0;
        font-size: 0.75rem;
        line-height: 16px;
        color: var(--inistate-text-secondary);
      }
      .user-info-phone {
        font-size: 0.75rem;
        line-height: 16px;
        color: var(--inistate-text-secondary);
        .user-info-phone-icon { font-size: 14px; margin-right: 6px; }
        .user-info-phone-number { font-size: 0.75rem; }
      }
      .user-actions {
        min-width: 150px;
        padding-left: 12px;
        .user-action-btn {
          height: 28px;
          min-height: 28px;
          padding: 0 10px;
          font-size: 0.75rem;
          letter-spacing: 0;
          &.fts-btn-primary {
            background-color: var(--fts-primary) !important;
            border-color: var(--fts-primary) !important;
            color: #fff !important;
          }
          &.fts-btn-danger {
            border-color: transparent !important;
            color: var(--fts-default) !important;
            &:hover { color: var(--fts-danger) !important; }
          }
        }
        .user-status .fts-badge { font-size: 0.75rem; }
      }
      .user-action-dropdown {
        margin-left: 12px;
        opacity: 0.35;
        transition: opacity 0.15s ease;
      }
    }

    /* U4 — lobby tab: the switch row on a hairline, the code card as a row */
    .user-lobby-form {
      padding: 0 24px;
      .lobby-invitation-container { margin-bottom: 8px; }
      .lobby-invitation-control {
        min-height: 36px;
        align-items: center;
        border-bottom: 1px solid var(--fts-gray-100);
      }
      .user-form-toggle-label { font-size: 0.8125rem; }
      .user-form-toggle-helper,
      .user-form-helper {
        font-size: 0.75rem;
        line-height: 16px;
        color: var(--inistate-text-secondary);
      }
      .lobby-invitation-card {
        min-height: 28px;
        margin-left: 12px;
        padding: 0 8px;
        border-color: var(--fts-gray-200);
        border-radius: var(--inistate-rounded-4);
      }
      .lobby-invitation-label { font-size: 0.75rem; }
    }
  }

  /* the collapsed profile section under a row kept a 16px top margin (48 → 65px rows) */
  .user-list-body .user-list-users .user-list-wrapper .user-profile-section {
    margin-top: 0;
    padding: 0;
    /* the .user-profile-container inside is an empty flex box until the row is expanded;
       spacing only once it holds something */
    > * { margin: 0; }
    > *:has(*) { margin: 4px 0 8px; }
  }

  /* U4 — empty states: one line, no illustration (two components render them) */
  .user-list-body .user-list-empty {
    display: flex;
    flex-wrap: nowrap;
    height: auto;
    min-height: 36px;
    padding: 8px 24px;
    flex-direction: row;
    align-items: baseline;
    justify-content: flex-start;
    gap: 6px;
    text-align: left;
    width: auto;
    max-width: none;
    .user-list-empty__icon { display: none; }
    .user-list-empty__title {
      flex: none;
      margin: 0;
      font-size: 0.8125rem;
      line-height: 20px;
      font-weight: 500;
      color: var(--fts-text);
      white-space: nowrap;
    }
    .user-list-empty__description {
      margin: 0;
      min-width: 0;
      font-size: 0.75rem;
      line-height: 16px;
      color: var(--inistate-text-secondary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .fts-result-not-found {
    padding: 8px 24px;
    text-align: left;
    align-items: flex-start;
    .illustration { display: none; }
    .not-found-container { text-align: left; }
    .not-found-title {
      margin: 0 0 2px;
      font-size: 0.8125rem;
      line-height: 20px;
      font-weight: 500;
      color: var(--fts-text);
    }
    .not-found-content {
      font-size: 0.75rem;
      line-height: 16px;
      color: var(--inistate-text-secondary);
    }
  }
}

.fts-users {
  height: 100%;
  overflow: auto;
  color: var(--fts-text);
  .user-list-header {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 12;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-left: 0.5rem;
    margin-right: 0.5rem;
    padding: 0.75rem;
    border-radius: var(--inistate-rounded-8);
    &.user-list-header--scroll {
      box-shadow: var(--fts-elevation-2);
      background-color: var(--inistate-color-shade-white);
    }
    .user-list-header__l {
      flex: 1;
    }
    .user-list-header__r {
      margin-left: 1rem;
    }
    /* tabs */
    .user-list-tabs {
      @include flex-gap($gap: 1rem);
      padding-top: 0.25rem;
      .user-list-tab {
        border-bottom: 2px solid transparent;
        font-size: 0.875rem;
        transition: all 0.3s ease-in-out;
        cursor: pointer;
        &.user-list-tab--active {
          border-color: var(--fts-primary);
        }
      }
    }
  }
  .user-list-body {
    min-width: 768px;
    padding: 0 1rem 2rem;
    /* control */
    .user-list-control {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      .user-list-control__l {
        flex: 1;
        display: flex;
        align-items: center;
        .user-list-form-control:not(:last-child) {
          margin-right: 0.75rem;
        }
      }
      .user-list-control__r {
        display: flex;
        align-items: flex-start;
        margin-left: 1rem;
        > *:not(:last-child) {
          margin-right: 0.25rem;
        }
      }
    }
    /* form control */
    .user-list-form-control {
      max-width: 250px;
      border-radius: 0.25rem;
      font-size: 0.875rem;
    }
    /* filter */
    .user-list-control + .user-list-filter {
      padding-top: 0.75rem;
    }
    .user-list-filter {
      @include flex-gap($gap: 0.5rem);
      .user-list-filter-tag {
        padding: 0.125rem 0.5rem;
        border: 1px solid var(--fts-border);
        border-radius: 0.5rem;
        background-color: var(--fts-card-background);
        font-size: 0.75rem;
        line-height: 1rem;
        cursor: default;
      }
      .user-list-filter-tag-action {
        margin-left: 0.25rem;
        color: var(--fts-default);
        cursor: pointer;
      }
    }
    /* container */
    .user-list-container {
      margin-top: 1rem;
      .user-list-card {
        box-shadow: var(--fts-elevation-1);
        border-radius: 0.25rem;
        background-color: var(--fts-card-background);
      }
      .user-list-wrapper {
        padding: 1.25rem;
        &:not(:last-child) {
          border-bottom: 1px solid var(--fts-border);
        }
      }
    }
    /* invitation */
    .invitation-container {
      .invitation-wrapper {
        display: flex;
        align-items: flex-start;
        .invitation-wrapper__l {
          min-width: 0;
          flex: 1;
        }
        .invitation-wrapper__r {
          min-width: 220px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-left: 2rem;
        }
      }
      .invitation-wrapper-header {
        display: flex;
        align-items: center;
        margin-bottom: 0.5rem;
        > *:not(:last-child) {
          margin-right: 0.5rem;
        }
      }
      .invitation-target {
        @include text-overflow-ellipsis;
        font-size: 0.875rem;
        line-height: 1.25rem;
      }
      .invitation-profile {
        display: inline-flex;
        align-items: center;
        padding-left: 0.25rem;
        padding-right: 0.25rem;
        border-radius: 0.25rem;
        background-color: var(--fts-gray-200);
        .invitation-profile-emoji {
          margin-right: 0.25rem;
        }
        .invitation-profile-name {
          max-width: 100px;
          font-size: 0.75rem;
          line-height: 1.25rem;
        }
      }
      .invitation-link-wrapper {
        margin-bottom: 0.75rem;
        .invitation-link {
          font-size: $font-size;
          line-height: $line-height;
          color: var(--fts-default);
          cursor: pointer;
        }
      }
      .invitation-audit {
        display: flex;
        align-items: center;
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--fts-default);
        .profile-name {
          margin-left: 0.25rem;
        }
      }
      .invitation-state-wrapper {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }
      .invitation-expiry-date {
        margin-top: 0.125rem;
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--fts-default);
      }
    }
    /* users */
    .user-list-users {
      .user-list-wrapper {
        min-width: 0;
        display: flex;
        flex-direction: column;
        &:hover {
          background-color: rgba(var(--fts-primary-rgb), 0.085);
          &:first-child {
            border-top-left-radius: inherit;
            border-top-right-radius: inherit;
          }
          &:last-child {
            border-bottom-left-radius: inherit;
            border-bottom-right-radius: inherit;
          }
          .user-info-name {
            color: var(--fts-primary);
          }
        }
      }
      .user-upr-row {
        display: flex;
        align-items: flex-start;
      }
      .user-avatar {
        margin-right: 1rem;
      }
      .user-info {
        flex: 1;
        min-width: 0;
        @include flex-gap($display: flex, $gapTop: .75rem);
        .user-info-wrapper {
          display: flex;
          align-items: center;
        }
        .user-info-name-wrapper {
          min-width: 0;
          cursor: pointer;
          .user-info-name {
            @include text-overflow-ellipsis;
            font-size: 0.875rem;
            line-height: 1.5;
          }
        }
        .user-info-username {
          @include text-overflow-ellipsis;
          margin-top: 0.125rem;
          font-size: 0.875rem;
          color: var(--fts-default);
        }
        .user-info-phone {
          display: flex;
          align-items: center;
          line-height: 1.5;
          .user-info-phone-icon {
            margin-right: 0.575rem;
            font-size: 0.75rem;
            color: var(--fts-default);
          }
          .user-info-phone-number {
            @include text-overflow-ellipsis;
            font-size: 0.875rem;
          }
        }
        .user-roles {
          display: flex;
          min-width: 0;
          margin-left: .375rem;
          .user-role-wrapper {
            min-width: 0;
            cursor: default;
            &:not(:first-child) {
              margin-left: 0.375rem;
            }
          }
        }
      }
      .user-actions {
        min-width: 150px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding-left: 1rem;
        .user-action-btn {
          font-weight: 400;
          &:not(:first-child) {
            margin-left: 0.5rem;
          }
        }
        .user-status {
          display: flex;
          align-items: center;
          flex: 1;
          .user-status-locked {
            @include theme() {
              color: color.adjust(theme-get('fts-danger'), $lightness: -5%);
            }
            margin-left: 0.75rem;
            font-size: 0.75rem;
          }
        }
      }
      .user-profile-section {
        margin-top: 1rem;
        flex: 1 1 100%;
        .user-profile-container {
          @include flex-gap($gap: 0.575rem);
          .user-profile-name {
            font-weight: 400;
            color: var(--fts-text);
          }
          .user-profile-state {
            margin-right: 0.575rem;
            font-size: 0.575rem;
          }
          .user-profile-action {
            margin-left: 0.375rem;
            margin-right: 0;
            &:hover {
              color: var(--fts-text);
            }
            &:not(:hover) {
              color: var(--fts-default);
            }
          }
        }
      }
    }
  }
  /* user action dropdown */
  .user-action-dropdown {
    margin-left: 1.5rem;
    .user-action-toggle {
      font-size: 0.875rem;
    }
  }
  // user form
  .user-form-label {
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 1.5rem;
  }
  .user-form-helper {
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--fts-default);
  }
  .user-form-toggle-label {
    margin-left: 0.375rem;
  }
  .user-form-toggle-helper {
    margin-left: 0.25rem;
  }
  // lobby
  .user-lobby-form {
    .lobby-invitation-container {
      display: flex;
      align-items: flex-start;
      margin-bottom: 1.5rem;
    }
    .lobby-invitation-control {
      flex-shrink: 0;
    }
    .lobby-invitation-card {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: flex-start;
      margin-left: 1rem;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--fts-border);
      border-radius: 0.375rem;
    }
    .lobby-invitation-row {
      display: flex;
      align-items: flex-start;
    }
    .lobby-invitation-label {
      margin-right: 0.5rem;
      padding-top: 0.375rem;
      padding-bottom: 0.375rem;
      border-top: 1px solid transparent;
      font-weight: 500;
      font-size: 0.875rem;
      line-height: 1.5;
      white-space: nowrap;
    }
    .lobby-invitation-form-control {
      max-width: 120px;
      border-radius: 0.25rem;
    }
    .lobby-invitation-btn {
      min-width: 70px;
    }
    .lobby-invitation-form-control + .lobby-invitation-btn {
      margin-left: 0.5rem;
    }
    .lobby-invitation-link {
      flex: 1;
      margin-left: 0.5rem;
    }
  }
  .profile-toggle-label {
    font-weight: 400;
  }
  /* confirmation modal */
  .confirmation-desc {
    font-size: 1rem;
    line-height: 1.5rem;
  }
  .confirmation-subtext {
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: var(--fts-default);
  }
  .confirmation-action,
  .confirmation-clipboard {
    margin-top: 0.375rem;
  }
  // empty 
  .user-list-empty {
    width: 350px;
    max-width: 100%;
    text-align: center;
    margin-left: auto;
    margin-right: auto;

    &__icon {
      max-width: 100%;
      width: 160px;
      margin-bottom: 12px;
      margin-left: auto;
      margin-right: auto;
      > img {
        display: flex;
        align-items: center;
        justify-content: center;
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
        margin: auto;
      }
    }

    &__title {
      margin-bottom: 6px;
      font-weight: var(--inistate-font-weight-medium);
      font-size: var(--inistate-paragraph-text-16);
      line-height: var(--inistate-paragraph-text-16--line-height);
      color: var(--inistate-text-primary);
    }

    &__description {
      margin-bottom: 16px;
      font-size: var(--inistate-label-text-14);
      line-height: var(--inistate-label-text-14--line-height);
      color: var(--inistate-text-secondary);
    }
  }
}
/* user action dropdown menu */
.user-action-content.user-action-dropdown-menu {
  .user-action-option {
    display: flex;
    align-items: flex-start;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    .user-action-option-icon {
      margin-right: 0.575rem;
      color: var(--fts-default);
    }
    .user-action-option-r {
      .user-action-option-helper {
        margin-top: 0.125rem;
        font-size: 0.75rem;
        line-height: 1rem;
        color: var(--fts-default);
      }
    }
  }
}
/* profile dropdown menu */
.profile-content.profile-dropdown-menu {
  min-width: 200px;
  max-width: 200px;
  .profile-option {
    display: flex;
    align-items: center;
    .profile-option-emoji {
      margin-right: 0.375rem;
    }
    .profile-option-name {
      @include text-overflow-hidden;
    }
  }
}
</style>
